import { useCallback, useEffect, useState } from "react";
import { Eye, Hourglass, ShieldAlert, Unlock, WifiOff } from "lucide-react";
import { fetchOverview } from "../../lib/api";
import Stat from "./Stat";

const ICON = {
  stalled: Hourglass,
  paused: Hourglass,
  gate_ready: Unlock,
  consent: ShieldAlert,
  dormant: WifiOff,
};

const SEVERITY = {
  high: "border-[#b4232a] text-[#ff7b7b]",
  medium: "border-[#8a6a1f] text-[#e0b55c]",
  low: "border-[var(--line-hi)] text-zinc-500",
};

const ago = (s) => {
  if (s === null || s === undefined) return "—";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const Flag = ({ flag }) => {
  const Icon = ICON[flag.kind] || ShieldAlert;
  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2 py-1 font-data text-[9px] uppercase tracking-[0.14em] ${SEVERITY[flag.severity]}`}
    >
      <Icon size={9} strokeWidth={1.9} />
      {flag.label}
    </span>
  );
};

const Row = ({ r, onWatch }) => (
  <li
    data-testid={`overview-row-${r.subject}`}
    className="grid gap-4 border-b border-[var(--line)] px-5 py-5 transition-colors hover:bg-[rgba(255,255,255,0.02)] lg:grid-cols-[1.4fr_auto_auto_auto_auto]"
  >
    <div className="min-w-0">
      <div className="flex items-center gap-2.5">
        <span
          className={`h-[6px] w-[6px] rounded-full ${
            r.online ? "bg-emerald-400" : "bg-zinc-700"
          }`}
        />
        <p className="truncate text-sm text-zinc-100">{r.name}</p>
        {r.typing && <span className="label !text-[9px] !text-zinc-300">typing</span>}
        {r.paused && <span className="label !text-[9px]">paused</span>}
      </div>
      <p className="mt-1.5 truncate font-data text-[10px] text-zinc-600">
        {r.email} · {r.subject.slice(0, 12)}
      </p>
      {r.attention.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {r.attention.map((f) => (
            <Flag key={f.kind + f.label} flag={f} />
          ))}
        </div>
      )}
    </div>

    <div className="lg:text-right">
      <p className="label !text-[9px]">Confidence</p>
      <p className="mt-1 font-data text-lg tabular-nums leading-none text-zinc-100">
        {r.predictive_confidence}%
      </p>
    </div>

    <div className="lg:text-right">
      <p className="label !text-[9px]">Coverage</p>
      <p className="mt-1 font-data text-lg tabular-nums leading-none text-zinc-300">
        {r.coverage.filled}/{r.coverage.total ?? 46}
      </p>
    </div>

    <div className="lg:text-right">
      <p className="label !text-[9px]">Phase · seen</p>
      <p className="mt-1 font-data text-[11px] leading-none text-zinc-400">
        {r.phase} · {r.turns} turns
      </p>
      <p className="mt-1.5 font-data text-[10px] text-zinc-600">{ago(r.idle_seconds)}</p>
    </div>

    <div className="lg:text-right">
      <button
        data-testid={`overview-watch-${r.subject}`}
        onClick={() => onWatch(r.subject)}
        className="label inline-flex items-center gap-1.5 hover:text-zinc-100"
      >
        <Eye size={11} strokeWidth={1.7} /> watch
      </button>
    </div>
  </li>
);

/** The console's landing page: every engagement at a glance, worst first. */
export const ConsoleHome = ({ onWatch }) => {
  const [d, setD] = useState(null);
  const [onlyAttention, setOnlyAttention] = useState(false);

  const load = useCallback(() => fetchOverview().then(setD).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const rows = (d?.subjects || []).filter((r) => !onlyAttention || r.attention.length > 0);

  return (
    <div data-testid="console-home" className="space-y-8">
      <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-5">
        <Stat testid="overview-total" k="Engagements" v={d?.totals?.engagements ?? 0} />
        <Stat k="Online now" v={d?.totals?.online ?? 0} />
        <Stat
          testid="overview-attention"
          k="Need attention"
          v={d?.totals?.needs_attention ?? 0}
          sub={`${d?.totals?.stalled ?? 0} stalled`}
        />
        <Stat k="Gates open" v={d?.totals?.gates_open ?? 0} />
        <Stat
          k="Checked"
          v={d?.checked_at?.slice(11, 19) || "—"}
          sub="refreshes every 10s"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label">Sorted by what is most urgent</p>
        <button
          data-testid="overview-filter-btn"
          onClick={() => setOnlyAttention((v) => !v)}
          className={`label border border-[var(--line-hi)] px-3 py-2 transition-colors hover:text-zinc-100 ${
            onlyAttention ? "!text-zinc-100" : ""
          }`}
        >
          {onlyAttention ? "showing flagged only" : "show flagged only"}
        </button>
      </div>

      <ul className="border-t border-[var(--line)]">
        {rows.map((r) => (
          <Row key={r.subject} r={r} onWatch={onWatch} />
        ))}
        {rows.length === 0 && (
          <li className="px-5 py-6 font-data text-[11px] text-zinc-600">
            {d ? "Nothing flagged — everyone is where they should be." : "Reading the database…"}
          </li>
        )}
      </ul>
    </div>
  );
};

export default ConsoleHome;
